import { useEffect, useState } from 'react';
import { Clock, Search, Trash2, Loader2, CheckCircle, XCircle, Activity } from 'lucide-react';
import { authedFetch } from '@/lib/api';
import { logger } from '@/lib/logger';
import { cn, formatDuration, formatPercentage, getMetricColor } from '@/lib/utils';

interface RunHistoryProps {
  onSelectRun: (runId: string) => void;
  currentRunId?: string;
}

interface RunSummary {
  id: string;
  status: 'running' | 'completed' | 'failed';
  created_at: string;
  duration_ms: number;
  runs_per_dataset?: number;
  total_datasets?: number;
  completed_datasets?: number;
  phase2?: {
    f1: number;
    recall: number;
    precision: number;
  };
}

export function RunHistory({ onSelectRun, currentRunId }: RunHistoryProps) {
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const fetchRuns = async () => {
    try {
      const response = await authedFetch('/api/evaluation/search-eval/runs');
      const data = await response.json();
      setRuns(data.runs || []);
    } catch (error) {
      logger.error('Failed to fetch runs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (runId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteError(null);
    setConfirmDelete(runId);
  };

  const handleDeleteConfirm = async (runId: string) => {
    setDeleting(runId);
    setConfirmDelete(null);
    try {
      const response = await authedFetch(
        `/api/evaluation/search-eval/runs/${runId}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        setRuns((prev) => prev.filter((r) => r.id !== runId));
        setDeleteError(null);
      } else {
        const data = await response.json();
        setDeleteError(data.detail || 'Failed to delete run');
      }
    } catch {
      setDeleteError('Failed to delete run');
    } finally {
      setDeleting(null);
    }
  };

  useEffect(() => {
    fetchRuns();
  }, []);

  // Refresh when evaluation completes
  useEffect(() => {
    const handleEvaluationComplete = () => {
      fetchRuns();
    };
    window.addEventListener('evaluation-complete', handleEvaluationComplete);
    return () =>
      window.removeEventListener('evaluation-complete', handleEvaluationComplete);
  }, []);

  // Poll for updates while any run is in progress
  useEffect(() => {
    const hasRunning = runs.some((r) => r.status === 'running');
    if (!hasRunning) return;

    const interval = setInterval(fetchRuns, 5000);
    return () => clearInterval(interval);
  }, [runs]);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return (
      date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
      ' ' +
      date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    );
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-green-500/20 text-green-400">
            <CheckCircle className="h-3 w-3" />
            Done
          </span>
        );
      case 'running':
        return (
          <span className="flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
            <Activity className="h-3 w-3 animate-pulse" />
            Running
          </span>
        );
      case 'failed':
        return (
          <span className="flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">
            <XCircle className="h-3 w-3" />
            Failed
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">Loading runs...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="mb-4 flex items-center justify-between shrink-0">
        <h3 className="text-lg font-bold bg-linear-to-r from-primary to-orange-400 bg-clip-text text-transparent">
          Run History
        </h3>
        <Search className="h-5 w-5 text-primary" />
      </div>

      {deleteError && (
        <div className="mb-2 p-2 rounded-lg bg-destructive/20 border border-destructive/50 text-destructive text-xs flex items-center justify-between">
          <span>{deleteError}</span>
          <button
            onClick={() => setDeleteError(null)}
            className="ml-2 hover:text-foreground"
          >
            &times;
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto space-y-2 pr-1">
        {runs.length > 0 ? (
          runs.map((run) => {
            const f1 = run.phase2?.f1 ?? 0;
            const recall = run.phase2?.recall ?? 0;
            const precision = run.phase2?.precision ?? 0;
            const total = run.total_datasets ?? 0;
            const completed = run.completed_datasets ?? 0;

            return (
              <div
                key={run.id}
                onClick={() => onSelectRun(run.id)}
                className={cn(
                  'p-3 rounded-lg border backdrop-blur-xs cursor-pointer transition-all hover:scale-[1.01] group relative',
                  currentRunId === run.id
                    ? 'border-primary bg-primary/10 shadow-md shadow-primary/10'
                    : 'border-border/50 bg-card/30 hover:border-primary/30 hover:bg-card/50'
                )}
              >
                {/* Delete button / confirmation */}
                {confirmDelete === run.id ? (
                  <div
                    className="absolute top-2 right-2 flex items-center gap-1 bg-card border border-destructive/30 rounded-md px-2 py-1 z-10"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="text-xs text-destructive mr-1">
                      Delete?
                    </span>
                    <button
                      onClick={() => handleDeleteConfirm(run.id)}
                      className="text-xs px-1.5 py-0.5 rounded bg-destructive/20 text-destructive hover:bg-destructive/30"
                    >
                      Yes
                    </button>
                    <button
                      onClick={() => setConfirmDelete(null)}
                      className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground hover:bg-muted/80"
                    >
                      No
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={(e) => handleDeleteClick(run.id, e)}
                    disabled={deleting === run.id}
                    className="absolute top-2 right-2 p-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity bg-destructive/10 hover:bg-destructive/20 text-destructive disabled:opacity-50"
                    title="Delete run"
                  >
                    {deleting === run.id ? (
                      <div className="h-3.5 w-3.5 border-2 border-destructive border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                  </button>
                )}

                {/* Dataset count + runs indicator */}
                <div className="font-semibold text-sm text-foreground truncate pr-8 mb-2">
                  {total > 1 ? `${total} Datasets` : `${total} Dataset`}
                  {(run.runs_per_dataset ?? 1) > 1 && (
                    <span className="text-[10px] text-primary/70 font-medium ml-1.5">
                      &times;{run.runs_per_dataset}
                    </span>
                  )}
                  {run.status === 'running' && total > 0 && (
                    <span className="text-xs text-muted-foreground font-normal ml-1.5">
                      ({completed}/{total})
                    </span>
                  )}
                </div>

                {/* F1 Score (hero) */}
                <div className="flex items-center justify-between mb-2">
                  <div
                    className={cn(
                      'text-2xl font-bold tabular-nums',
                      run.status === 'completed'
                        ? getMetricColor(f1)
                        : 'text-muted-foreground'
                    )}
                  >
                    {run.status === 'completed' || (run.status === 'running' && completed > 0)
                      ? formatPercentage(f1)
                      : '--'}
                  </div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
                    F1
                  </div>
                </div>

                {/* Recall / Precision badges */}
                {(run.status === 'completed' || (run.status === 'running' && completed > 0)) && (
                  <div className="flex gap-2 mb-2">
                    <span
                      className={cn(
                        'text-[10px] font-medium px-1.5 py-0.5 rounded bg-card/80',
                        getMetricColor(recall)
                      )}
                    >
                      R: {formatPercentage(recall, 0)}
                    </span>
                    <span
                      className={cn(
                        'text-[10px] font-medium px-1.5 py-0.5 rounded bg-card/80',
                        getMetricColor(precision)
                      )}
                    >
                      P: {formatPercentage(precision, 0)}
                    </span>
                  </div>
                )}

                {/* Progress bar while running */}
                {run.status === 'running' && total > 0 && (
                  <div className="mb-2">
                    <div className="w-full h-1.5 bg-card/80 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-linear-to-r from-primary to-orange-400 rounded-full transition-all duration-500"
                        style={{ width: `${(completed / total) * 100}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Bottom: status, duration, timestamp */}
                <div className="flex items-center gap-2 flex-wrap">
                  {getStatusBadge(run.status)}
                  {run.duration_ms > 0 && (
                    <span className="text-[10px] text-muted-foreground">
                      {formatDuration(run.duration_ms)}
                    </span>
                  )}
                  <span className="flex items-center gap-1 text-[10px] text-muted-foreground ml-auto">
                    <Clock className="h-3 w-3" />
                    {formatTimestamp(run.created_at)}
                  </span>
                </div>
              </div>
            );
          })
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center p-6">
            <Search className="h-12 w-12 text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">
              No evaluation runs found
            </p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              Start an evaluation to see history
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
